function Pagination({ page, totalPages, onPageChange, hasMore = false }) {
  // For blob storage mode, we might not know total pages
  const effectiveTotalPages = hasMore ? Math.max(totalPages, page + 1) : totalPages;
  const isLastPage = !hasMore && page === totalPages;

  if (effectiveTotalPages <= 1 && !hasMore) return null;

  // Calculate visible page numbers
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5; // Max pages to show
    let start = Math.max(1, page - Math.floor(showPages / 2));
    let end = Math.min(effectiveTotalPages, start + showPages - 1);

    // Adjust start if end is at max
    if (end === effectiveTotalPages) {
      start = Math.max(1, end - showPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3">
      {/* Mobile: Simple prev/next */}
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={isLastPage}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>

      {/* Desktop: Full pagination */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Page <span className="font-medium text-gray-900">{page}</span>
            {!hasMore && effectiveTotalPages > 1 && (
              <> of <span className="font-medium text-gray-900">{effectiveTotalPages.toLocaleString()}</span></>
            )}
            {hasMore && ' (more available)'}
          </p>
        </div>

        <nav className="flex items-center space-x-1">
          {/* First page */}
          <button
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="First page"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          {/* Previous page */}
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Previous page"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Page numbers */}
          {pageNumbers[0] > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className="px-3 py-1 text-sm rounded text-gray-700 hover:bg-gray-100"
              >
                1
              </button>
              {pageNumbers[0] > 2 && <span className="px-2 text-gray-400">...</span>}
            </>
          )}

          {pageNumbers.map((num) => (
            <button
              key={num}
              onClick={() => onPageChange(num)}
              className={`px-3 py-1 text-sm rounded ${
                num === page
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {num}
            </button>
          ))}

          {pageNumbers[pageNumbers.length - 1] < effectiveTotalPages && !hasMore && (
            <>
              {pageNumbers[pageNumbers.length - 1] < effectiveTotalPages - 1 && (
                <span className="px-2 text-gray-400">...</span>
              )}
              <button
                onClick={() => onPageChange(effectiveTotalPages)}
                className="px-3 py-1 text-sm rounded text-gray-700 hover:bg-gray-100"
              >
                {effectiveTotalPages}
              </button>
            </>
          )}

          {/* Next page */}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={isLastPage}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Next page"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Last page - only show when we know total pages */}
          {!hasMore && (
            <button
              onClick={() => onPageChange(effectiveTotalPages)}
              disabled={isLastPage}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Last page"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </nav>
      </div>
    </div>
  );
}

export default Pagination;
